import type { SupabaseClient } from "@supabase/supabase-js";

import {
  ALERT_CHANNELS,
  ALERT_CHANNEL_LABELS,
  SMS_AVAILABLE,
  alertEventDefinition,
  alertEventLabel,
  fetchAlertChannelPolicy,
  isMissingPolicyRpc,
  type AlertChannelPolicyRow,
  type AlertQuietHours,
} from "./alert-channel-policy";

// Personal alert channel preferences. Existing events are additive over the account floor.
// Messages push is the locked opt-out exception.
//
// For existing alert events, the account sets a floor and one signed-in member can only add
// channels. Messages push is resolved as account on AND member on, so false is a real opt-out.
// Every claim this surface makes has to survive the worker resolver, so the honest statements are:
//
//   - SOS and duress deliver in-app and by push unconditionally, whatever is set here.
//   - Account quiet hours still hold ordinary push, and hold email for every event kind.
//   - Personal email additions are refused for SOS and duress.
//   - Messages email is unavailable and a member may switch Messages push off.
//   - SMS has no delivery path at all.
//
// Reads and writes are the two shipped RPCs only; the browser never touches
// app_private.member_alert_channel_preferences, which has no grants for any client role.

export const LIST_MY_ALERT_CHANNEL_PREFERENCES_RPC = "list_my_alert_channel_preferences";
export const UPSERT_MY_ALERT_CHANNEL_PREFERENCE_RPC = "upsert_my_alert_channel_preference";

/**
 * The event catalog the shipped migration pins, in the order its list RPC returns. Used to assert
 * the copy below covers every key - NOT to synthesise rows. Rows come from the server.
 */
export const MEMBER_ALERT_EVENT_KEYS = [
  "sos",
  "duress",
  "missed_checkpoint",
  "schedule_change",
  "feed_require_ack",
  "feed_announcement",
  "geofence",
  "field_message",
] as const;

/**
 * Life-safety events. Their in-app and push delivery is forced true by the resolver, and their
 * personal email addition is refused by both a CHECK constraint and an explicit RAISE.
 */
const SAFETY_EVENT_KEYS: ReadonlySet<string> = new Set(["sos", "duress"]);

export function isSafetyEventKey(eventKey: string): boolean {
  return SAFETY_EVENT_KEYS.has(eventKey);
}

/** True when this member may add email for this event at all. */
export function personalEmailAvailable(eventKey: string): boolean {
  return !isSafetyEventKey(eventKey) && eventKey !== "field_message";
}

export interface MemberAlertChannelPreferenceRow {
  readonly eventKey: string;
  readonly inApp: boolean;
  readonly push: boolean;
  readonly email: boolean;
  readonly sms: boolean;
}

export type MemberAlertChannelPreferencesLoad =
  | { readonly status: "ready"; readonly rows: readonly MemberAlertChannelPreferenceRow[] }
  | { readonly status: "unavailable"; readonly reason: string };

export const MEMBER_ALERT_PREFS_UNAVAILABLE =
  "Personal alert channel preferences are not available on this server yet. Nothing about how you are notified has changed.";

export const MEMBER_ALERT_PREFS_READ_FAILED = "Could not load your alert channel preferences.";

/**
 * The account panel labels `sos` and `duress` with one shared "Duress or SOS" string, because it
 * sets one floor for both. The member list RPC returns them as two rows, so they need two names -
 * a matrix with the same row title twice is unreadable, and two switches with the same accessible
 * name are worse. Every other key reuses the account copy so the two panels cannot drift apart.
 */
const MEMBER_EVENT_LABEL_OVERRIDES: Record<string, string> = {
  sos: "SOS",
  duress: "Duress",
  field_message: "Messages",
};

const MEMBER_EVENT_DEFINITION_OVERRIDES: Record<string, string> = {
  sos: "Always reaches you in-app and by push.",
  duress: "Always reaches you in-app and by push.",
  field_message: "Direct and crew. Quiet hours apply.",
};

export function memberAlertEventLabel(eventKey: string): string {
  return MEMBER_EVENT_LABEL_OVERRIDES[eventKey] ?? alertEventLabel(eventKey);
}

export function memberAlertEventDefinition(eventKey: string): string {
  return MEMBER_EVENT_DEFINITION_OVERRIDES[eventKey] ?? alertEventDefinition(eventKey);
}

/**
 * The list RPC returns `event_key, in_app, push, email, sms` - NOT the `channel_` prefixed columns
 * the account policy RPC returns. Both clamps below mirror a server rule rather than trusting the
 * payload: SMS has no delivery path, and personal safety email is refused by a CHECK constraint,
 * so a true in either position would be a server fault. Rendering it as on would tell the member
 * they had added something the server will never send.
 */
export function mapMemberAlertChannelPreferenceRow(raw: unknown): MemberAlertChannelPreferenceRow | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const eventKey = typeof row.event_key === "string" ? row.event_key.trim() : "";
  if (eventKey.length === 0) return null;
  return {
    eventKey,
    inApp: row.in_app === true,
    push: row.push === true,
    email: personalEmailAvailable(eventKey) && row.email === true,
    sms: SMS_AVAILABLE && row.sms === true,
  };
}

/**
 * The caller's own preferences for one account. `p_account_id` is the only argument: the RPC binds
 * the row to `auth.uid()` server-side, so no user id is ever sent from the browser and no member
 * can address another member's preferences.
 */
export async function fetchMyAlertChannelPreferences(
  client: SupabaseClient,
  accountId: string,
): Promise<MemberAlertChannelPreferencesLoad> {
  let rowsRaw: unknown;
  try {
    const { data, error } = await client.rpc(LIST_MY_ALERT_CHANNEL_PREFERENCES_RPC, {
      p_account_id: accountId,
    });
    if (error !== null) {
      if (isMissingPolicyRpc(error)) {
        return { status: "unavailable", reason: MEMBER_ALERT_PREFS_UNAVAILABLE };
      }
      // Membership and auth refusals land here. They carry server sentences that would turn this
      // read into an oracle, so the surface says one fixed thing instead.
      throw new Error(MEMBER_ALERT_PREFS_READ_FAILED);
    }
    rowsRaw = data;
  } catch (caught) {
    throw caught instanceof Error ? caught : new Error(MEMBER_ALERT_PREFS_READ_FAILED);
  }

  const rows: MemberAlertChannelPreferenceRow[] = [];
  if (Array.isArray(rowsRaw)) {
    for (const raw of rowsRaw) {
      const row = mapMemberAlertChannelPreferenceRow(raw);
      if (row !== null) rows.push(row);
    }
  }
  return { status: "ready", rows };
}

export type MemberAlertPrefWriteOutcome = { ok: true } | { ok: false; reason: string };

export const SAFE_MEMBER_ALERT_PREF_FAILURE =
  "Couldn't save that notification change - please try again.";

/**
 * Verbatim refusal sentences raised by `upsert_my_alert_channel_preference` in migration
 * 20260811120000_member_alert_channel_preferences.sql, transcribed from the shipped SQL. Anything
 * else - a missing-RPC error, and deliberately the internal "caller mismatch" guard - collapses to
 * the generic sentence, so the surface cannot be used as an error oracle. This mirrors the sibling
 * allowlist in alert-channel-policy.ts.
 */
export const KNOWN_MEMBER_ALERT_PREF_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to manage personal alert channel preferences.",
  "Account id is required to manage personal alert channel preferences.",
  "Active account membership is required to manage personal alert channel preferences.",
  "Alert event key is not supported.",
  "Personal alert channel selections are required.",
  "SMS alert delivery is not available.",
  "Personal email additions are not available for SOS or duress alerts.",
  "Email delivery is not available for Messages.",
]);

export function safeMemberAlertPrefReason(error: unknown): string {
  const message =
    error !== null && typeof error === "object" && typeof (error as { message?: unknown }).message === "string"
      ? (error as { message: string }).message
      : "";
  return KNOWN_MEMBER_ALERT_PREF_FAILURES.has(message) ? message : SAFE_MEMBER_ALERT_PREF_FAILURE;
}

/**
 * One event row for the caller. `p_sms` is hard-wired false and safety email is forced false
 * before the call, so the browser never sends a value the server has to refuse. No user id is in
 * the payload: the RPC resolves the owner from `auth.uid()`.
 */
export async function upsertMyAlertChannelPreference(
  client: SupabaseClient,
  accountId: string,
  row: MemberAlertChannelPreferenceRow,
): Promise<MemberAlertPrefWriteOutcome> {
  try {
    const { error } = await client.rpc(UPSERT_MY_ALERT_CHANNEL_PREFERENCE_RPC, {
      p_account_id: accountId,
      p_event_key: row.eventKey,
      p_in_app: row.inApp,
      p_push: row.push,
      p_email: personalEmailAvailable(row.eventKey) && row.email,
      p_sms: false,
    });
    return error === null ? { ok: true } : { ok: false, reason: safeMemberAlertPrefReason(error) };
  } catch {
    return { ok: false, reason: SAFE_MEMBER_ALERT_PREF_FAILURE };
  }
}

/** Rows whose channels differ from what the server returned. Save writes only these. */
export function changedMemberPreferenceRows(
  saved: readonly MemberAlertChannelPreferenceRow[],
  draft: readonly MemberAlertChannelPreferenceRow[],
): MemberAlertChannelPreferenceRow[] {
  const savedByKey = new Map(saved.map((row) => [row.eventKey, row]));
  return draft.filter((row) => {
    const before = savedByKey.get(row.eventKey);
    if (before === undefined) return true;
    return before.inApp !== row.inApp || before.push !== row.push || before.email !== row.email || before.sms !== row.sms;
  });
}

export interface AccountAlertFloor {
  readonly rows: readonly AlertChannelPolicyRow[];
  readonly quietHours: AlertQuietHours;
}

export interface MemberAlertChannelPreferencesView {
  readonly preferences: MemberAlertChannelPreferencesLoad;
  /** Read-only explanation of what the account already sends, or null when it cannot be shown. */
  readonly floor: AccountAlertFloor | null;
}

/**
 * The member's own preferences, plus the account floor when - and only when - the caller already
 * holds `policy.view`. Without that capability the policy RPC is not called at all: a member does
 * not gain a read of account configuration by opening their own notification settings.
 */
export async function fetchMemberAlertChannelPreferencesView(
  client: SupabaseClient,
  accountId: string,
  canViewAccountFloor: boolean,
): Promise<MemberAlertChannelPreferencesView> {
  const preferences = await fetchMyAlertChannelPreferences(client, accountId);
  if (!canViewAccountFloor) {
    return { preferences, floor: null };
  }
  try {
    const policy = await fetchAlertChannelPolicy(client, accountId);
    return {
      preferences,
      floor: policy.status === "ready" ? { rows: policy.rows, quietHours: policy.quietHours } : null,
    };
  } catch {
    // The floor is an explanation, not this member's own data. A policy read that fails must not
    // take down the panel they came here to use; the explanation is simply omitted.
    return { preferences, floor: null };
  }
}

/**
 * What the account already sends for one event, for the muted line under the event name. Returns
 * null for an event the account matrix does not carry - saying nothing beats guessing "nothing".
 */
export function accountFloorSentence(row: AlertChannelPolicyRow | undefined): string | null {
  if (row === undefined) return null;
  const labels = ALERT_CHANNELS.filter(
    (channel) => channel !== "sms" && row[channel],
  ).map((channel) => ALERT_CHANNEL_LABELS[channel]);
  return labels.length === 0
    ? "Your account sends nothing for this on its own."
    : `Your account already sends: ${labels.join(", ")}.`;
}

/**
 * The quiet-hours sentence, or null when quiet hours are off or incompletely configured. Phrased
 * from the resolver: quiet hours hold ordinary push and hold email for EVERY event kind, while
 * in-app is never held and safety push is forced through.
 */
export function quietHoursSentence(quietHours: AlertQuietHours): string | null {
  if (!quietHours.enabled || quietHours.startLocal === null || quietHours.endLocal === null) {
    return null;
  }
  const zone = quietHours.timeZone === null ? "" : ` ${quietHours.timeZone}`;
  return `Your account holds push for ordinary events, and email for every event, between ${quietHours.startLocal} and ${quietHours.endLocal}${zone}. SOS and duress still reach you in-app and by push.`;
}
